import { AuthConfig, AuthProfile, AuthUser } from "../type";
import jwtDecode from "jwt-decode";
import { logInfo, logWarn } from "./logger";
import SessionServiceClient from "../sessionsvc/SessionServiceClient";
import AmplifyAuth from "@aws-amplify/auth";

export default class UserTokenManager {
  private readonly userKey : string;

  private readonly authConfig : AuthConfig;

  constructor(userKey : string, authConfig : AuthConfig) {
    this.userKey = userKey;
    this.authConfig = authConfig;
  }

  /**
   * Takes authentication tokens as input and store them in local storage.
   */
  public setUserToStorage(idToken: string, accessToken: string, refreshToken: string, time: number) : void {
    let authProfile : AuthProfile | null = null;
    try {
      authProfile = jwtDecode(idToken);
    } catch (e) {
      // TODO handle exception better as we use this method in cognito flow
      // as currently this method is only used in Midway flow and AuthProfile object is not really used
      logWarn("AuthManager", "JWT token cannot be decoded")
    }

    const user : AuthUser = {
      id_token: idToken,
      access_token: accessToken,
      refresh_token: refreshToken,
      scope: "",
      token_type: "token",
      profile: authProfile,
      expires_at: time,
    };
    const userkey = this.userKey;

    if (userkey) {
      window.localStorage.setItem(userkey, JSON.stringify(user));
    }
  }

  /**
   * Retrieves user object and authentication tokens from browser local storage if exists.
   */
  public async getUserFromStorage() : Promise<AuthUser | null> {
    // New login page uses Amplify library to authenticate both internal and external user
    // and the tokens are stored with different keys in browser local storage.
    // To keep backward compatibility, the following code maps them to use the same key staring with "oidc"
    if (this.userKey && this.authConfig && this.authConfig.useSelfHostedLoginUI == true) {
      try {
        const amplifySession = await AmplifyAuth.currentSession();
        this.setUserToStorage(
          amplifySession.getIdToken().getJwtToken(),
          amplifySession.getAccessToken().getJwtToken(),
          amplifySession.getRefreshToken().getToken(),
          amplifySession.getIdToken().getExpiration());
      } catch (err) {
        // if there is error thrown, fallback to previous implementation to retrieve user tokens, there are 2 purposes:
        // 1. Not force user log out during the switch of new UI
        // 2. Maintain the current UI integration test behaviors
        logWarn('AuthManager', 'Current user session cannot be fetched by AmplifyAuth with error: ' + err);
      }
    }

    if (this.userKey && window.localStorage.getItem(this.userKey)) {
      const authUserString = window.localStorage.getItem(this.userKey);
      if (authUserString) {
        const user = JSON.parse(authUserString);
        const expireTime = user.expires_at;
        const currentTime = new Date().getTime()/1000;
        if (currentTime >= expireTime) {
          logInfo('AuthManager.token has expired', 'current time ' + currentTime.toString() + ' expire time '+ expireTime.toString());
          window.localStorage.removeItem(this.userKey);
          window.localStorage.removeItem(SessionServiceClient.sessSvcKey);
          window.location.assign(this.authConfig.redirectSignIn);
          return null;
        }
        return user;
      }
    }
    return null;
  }
}